import {
  type EmployeeAnswerFragment,
  EmployeeCompleteCurrentTestDocument,
  EmployeeSaveAnswerDocument,
  EmployeeSyllabusDocument,
  type EmployeeSyllabusQuery,
  EmployeeUpdateCurrentVideoProgressDocument,
  type EmployeeUpdateCurrentVideoProgressMutationVariables,
  EmployeeVerifyCurrentTestDocument,
  EmployeeWatchQuestionDocument,
  type SaveAnswerInput,
  type WatchQuestionInput
} from "app/gql/graphqlSchema";
import {
  ApiState,
  type Loadable,
  loadedOk,
  LOADING_STATE_ERROR,
  LOADING_STATE_NA,
  selectMaybeLoaded
} from "app/state/common/loadable";
import type {MaybeNil} from "app/utils/types";
import {query} from "app/gql/client";
import {isNotNil} from "app/utils/stdlib";
import {createWithEqualityFn as zustandCreate} from "zustand/traditional";
import {immer} from "zustand/middleware/immer";
import type {ElementOf} from "ts-essentials";
import {getInProgressVideoLesson} from "app/employee/state/syllabus/inProgressVideoLessons";
import type {RefresherEventDetail} from "@ionic/react";
import {DateTime} from "luxon";

export type Syllabus = EmployeeSyllabusQuery["employeeSyllabus"];
// export type ISyllabusAssignment = ElementOf<ISyllabus["past"]>;
// export type ISyllabusVideoLesson = ElementOf<ISyllabus["pending"]>;
// export type ISyllabusCurrentAssignment = NonNullable<ISyllabus["current"]>;
// export type ISyllabusAnswer = ElementOf<ISyllabusQuestion["answers"]>;
type SyllabusTermProgress = Syllabus["termProgress"];
export type SyllabusTerm = ElementOf<SyllabusTermProgress["terms"]>;
export type SyllabusTermPlan = ElementOf<SyllabusTerm["termPlans"]>;

export interface SyllabusPlanWithTerm {
  term: SyllabusTerm;
  termPlan: SyllabusTermPlan;
}

export type SyllabusTermVideoLesson = ElementOf<SyllabusTermPlan["lessons"]>;
export type SyllabusCompletedVideoLesson = Extract<
  SyllabusTermVideoLesson,
  {__typename: "TermPlanCompletedVideoLesson"}
>;
export type SyllabusInProgressVideoLesson = Extract<
  SyllabusTermVideoLesson,
  {__typename: "TermPlanInProgressVideoLesson"}
>;
export type SyllabusTermPlanPendingVideoLesson = Extract<
  SyllabusTermVideoLesson,
  {__typename: "TermPlanPendingVideoLesson"}
>;
export type SyllabusPlanVideoLessonKind = SyllabusTermVideoLesson["__typename"];

export type SyllabusQuestion = ElementOf<SyllabusInProgressVideoLesson["videoLesson"]["test"]["questions"]>;
export type TestResult = SyllabusInProgressVideoLesson["testResult"];

export enum RedirectType {
  NONE = "NONE",
  TEST = "TEST",
  TEST_RESULT = "TEST_RESULT",
  NEXT_TOPIC = "NEXT_TOPIC"
}

export interface SyllabusState {
  syllabus: Loadable<Syllabus>;
  saveAnswerState: ApiState;
  watchQuestionState: ApiState;
  redirect: RedirectType;
  lastRefreshMs: MaybeNil<number>;
}

const initialState: SyllabusState = {
  syllabus: LOADING_STATE_NA,
  saveAnswerState: ApiState.NA,
  watchQuestionState: ApiState.NA,
  redirect: RedirectType.NONE,
  lastRefreshMs: null
};

export const useSyllabusStore = zustandCreate(
  immer(() => initialState),
  Object.is
);

type UpdateProgressArg = EmployeeUpdateCurrentVideoProgressMutationVariables["input"];
export const updateCurrentVideoProgress = async (arg: UpdateProgressArg) => {
  await query(EmployeeUpdateCurrentVideoProgressDocument, {input: arg});
};

export const getEmployeeSyllabus = async () => {
  try {
    useSyllabusStore.setState((state) => {
      state.syllabus.loadingState = ApiState.LOADING;
      state.lastRefreshMs = DateTime.now().toMillis();
    });
    // await delay(2000);
    const clientResult = await query(EmployeeSyllabusDocument);
    useSyllabusStore.setState((state) => {
      state.syllabus = loadedOk(clientResult.employeeSyllabus);
    });
  } catch (e) {
    useSyllabusStore.setState((state) => {
      state.syllabus = LOADING_STATE_ERROR;
    });
    throw e;
  }
};

export const updateEmployeeSyllabus = (newSyllabus: Syllabus) => {
  useSyllabusStore.setState((state) => {
    state.syllabus = loadedOk(newSyllabus);
  });
};

export type SaveAnswer = SaveAnswerInput;
export const saveAnswer = async (input: SaveAnswer) => {
  updateAnswer(input);
  try {
    useSyllabusStore.setState((state) => {
      state.saveAnswerState = ApiState.LOADING;
    });
    await query(EmployeeSaveAnswerDocument, {input});
    useSyllabusStore.setState((state) => {
      state.saveAnswerState = ApiState.OK;
    });
  } catch (e) {
    useSyllabusStore.setState((state) => {
      state.saveAnswerState = ApiState.ERROR;
    });
    throw e;
  }
};

export const updateAnswer = (answer: SaveAnswer) => {
  useSyllabusStore.setState((state) => {
    if (state.syllabus.loadingState === ApiState.OK) {
      const {answerId, questionId} = answer;
      const videoLesson = getInProgressVideoLesson(selectMaybeLoaded(state.syllabus)?.termProgress?.terms ?? []);
      videoLesson?.lesson.videoLesson.test.questions.forEach((q) => {
        if (q.id === questionId) {
          q.employeeAnswer = {
            answerId,
            employeeAnswerId: "",
            watchCount: 0,
            correct: null
          };
        }
      });
    }
  });
};

export type WatchQuestion = WatchQuestionInput;
export const watchQuestion = async (input: WatchQuestion) => {
  try {
    useSyllabusStore.setState((state) => {
      state.watchQuestionState = ApiState.LOADING;
    });
    const clientResult = await query(EmployeeWatchQuestionDocument, {input});
    useSyllabusStore.setState((state) => {
      updateEmployeeAnswer(state.syllabus, clientResult.employeeWatchQuestion.employeeAnswer);
      state.watchQuestionState = ApiState.OK;
    });
  } catch (e) {
    useSyllabusStore.setState((state) => {
      state.watchQuestionState = ApiState.ERROR;
    });
    throw e;
  }
};

export const completeCurrentTest = async () => {
  try {
    useSyllabusStore.setState((state) => {
      state.syllabus.loadingState = ApiState.LOADING;
      state.lastRefreshMs = DateTime.now().toMillis();
    });
    const clientResult = await query(EmployeeCompleteCurrentTestDocument);
    useSyllabusStore.setState((state) => {
      state.syllabus = loadedOk(clientResult.employeeCompleteCurrentTest.syllabus);
    });
  } catch (e) {
    useSyllabusStore.setState((state) => {
      state.syllabus.loadingState = ApiState.ERROR;
    });
    throw e;
  }
};

export const verifyCurrentTest = async () => {
  try {
    useSyllabusStore.setState((state) => {
      state.syllabus.loadingState = ApiState.LOADING;
      state.lastRefreshMs = DateTime.now().toMillis();
    });
    const clientResult = await query(EmployeeVerifyCurrentTestDocument);
    useSyllabusStore.setState((state) => {
      state.syllabus = loadedOk(clientResult.employeeVerifyCurrentTest.syllabus);
    });
  } catch (e) {
    useSyllabusStore.setState((state) => {
      state.syllabus.loadingState = ApiState.ERROR;
    });
    throw e;
  }
};

const updateEmployeeAnswer = (syllabus: Loadable<Syllabus>, newAnswer: MaybeNil<EmployeeAnswerFragment>) => {
  if (syllabus.loadingState === ApiState.OK && isNotNil(newAnswer)) {
    const newEmployeeAnswerId = newAnswer.employeeAnswerId;
    const videoLesson = getInProgressVideoLesson(syllabus.value.termProgress.terms)?.lesson.videoLesson;

    videoLesson?.test.questions.forEach((q) => {
      if (q.employeeAnswer?.employeeAnswerId === newEmployeeAnswerId) {
        q.employeeAnswer = newAnswer;
      }
    });
  }
};

export const redirectTo = (redirectType: RedirectType) => {
  useSyllabusStore.setState((state) => {
    state.redirect = redirectType;
  });
};

export const popRedirect = () => {
  const ret = useSyllabusStore.getState().redirect;
  useSyllabusStore.setState((state) => {
    state.redirect = RedirectType.NONE;
  });
  return ret;
};

export const refreshSyllabus = async (event?: CustomEvent<RefresherEventDetail>) => {
  const style = "color: blue; font-weight: bold;";
  console.log("%cReloading syllabus", style);
  try {
    await getEmployeeSyllabus();
    console.log("%cReloaded syllabus ✅", style);
  } catch (e) {
    console.warn("%cReloaded syllabus ❌", e);
    throw e;
  } finally {
    event?.detail.complete();
  }
};

export const reset = () => useSyllabusStore.setState(initialState, true);
